import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import ModalCenter from '@/common/components/modal/ModalCenter';
import { useBillingStore } from '@/store/useBillingStore';
import { billingService } from '@/services/billingService';
import { semanticColors, colorTokens } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Check, Sparkles, Loader2, ArrowRight, MessageSquare, Building2, Crown, ShieldCheck, Zap, X, Send, CheckCircle2, CreditCard, RotateCcw, ChevronRight, FileText } from 'lucide-react';
import { toastUtils } from '@/common/utils/toastUtils';
import { BillingProductItem } from '@/types/billing';

interface PlanResult {
  type: 'paid' | 'free';
  eventType?: 'NONE' | 'CHARGE' | 'REFUND';
  amount?: number;
  message?: string;
  planName: string;
}

interface PlanSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentProductId?: number;
  recommendedProductId?: number;
  companyId: number;
  onCardRegisterRequest: () => void;
  mode?: 'create' | 'change';
  subscriptionId?: number;
  onComplete?: () => void;
}

/**
 * 서비스 플랜 선택 모달 - 대대적인 디자인 개편 버전
 * 가시성과 프리미엄 감성을 극대화한 UI
 */
const PlanSelectionModal: React.FC<PlanSelectionModalProps> = ({
  isOpen,
  onClose,
  currentProductId,
  recommendedProductId,
  companyId,
  onCardRegisterRequest,
  mode = 'change',
  subscriptionId,
  onComplete,
}) => {
  const { billingStatus, loadBillingData } = useBillingStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [localPlans, setLocalPlans] = useState<BillingProductItem[]>([]);
  const [isPlansLoading, setIsPlansLoading] = useState(false);
  const [consultationModal, setConsultationModal] = useState<{
    isOpen: boolean;
    productId: number | null;
    planName: string;
  }>({ isOpen: false, productId: null, planName: '' });
  const [consultationMessage, setConsultationMessage] = useState('');
  const [isSubmittingConsultation, setIsSubmittingConsultation] = useState(false);
  const [planResult, setPlanResult] = useState<PlanResult | null>(null);
  const [isRefundPolicyOpen, setIsRefundPolicyOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || planResult) return;

    const loadPlans = async () => {
      setIsPlansLoading(true);
      try {
        if (!billingStatus) {
          await loadBillingData(companyId);
        }

        if (mode === 'change' && subscriptionId) {
          const changeablePlans = await billingService.getChangeablePlans(companyId, subscriptionId);
          setLocalPlans(changeablePlans);
        } else {
          const allPlans = await billingService.getPlans('INVESTOR');
          setLocalPlans(allPlans);
        }
      } catch {
        toastUtils.error('플랜 정보를 불러오는데 실패했습니다.');
      } finally {
        setIsPlansLoading(false);
      }
    };

    loadPlans();
  }, [isOpen, companyId, mode, subscriptionId, billingStatus, loadBillingData, planResult]);

  const isLoading = isPlansLoading;
  const plans = localPlans;

  const selfManagedPlans = plans.filter(p => p.id <= 4);
  const carePlans = plans.filter(p => p.id === 5);

  const handleOpenConsultationModal = (productId: number, planName: string) => {
    setConsultationModal({ isOpen: true, productId, planName });
    setConsultationMessage('');
  };

  const handleCloseConsultationModal = () => {
    setConsultationModal({ isOpen: false, productId: null, planName: '' });
    setConsultationMessage('');
  };

  const handleSubmitConsultation = async () => {
    if (!consultationModal.productId) return;

    setIsSubmittingConsultation(true);
    try {
      const response = await billingService.createConsultationInquiry({
        companyId,
        productId: consultationModal.productId,
        message: consultationMessage.trim() || undefined,
      });
      if (response.success) {
        toastUtils.success(response.message || '별도 협의 문의가 접수되었습니다.');
        handleCloseConsultationModal();
      } else {
        toastUtils.error(response.message || '문의 접수에 실패했습니다.');
      }
    } catch {
      toastUtils.error('문의 접수 중 오류가 발생했습니다.');
    } finally {
      setIsSubmittingConsultation(false);
    }
  };

  const handleSelectPlan = async (productId: number, isFree: boolean, isConsultation: boolean, planName: string) => {

    console.log('isConsultation', isConsultation);
    console.log('planName', planName);
    console.log('mode', mode);
    console.log('isFree', isFree);


    if (isConsultation) {
      handleOpenConsultationModal(productId, planName);
      return;
    }

    if (!isFree && !billingStatus?.hasBillingKey) {
      toastUtils.info('유료 플랜을 이용하려면 결제 수단 등록이 필요합니다.');
      onCardRegisterRequest();
      return;
    }
      
    setIsProcessing(true);
    try {
      if (mode === 'change' && subscriptionId) {
        const response = await billingService.changePlan(companyId, subscriptionId, { targetProductId: productId });
        if (response.success) {
          setPlanResult({
            type: 'paid',
            eventType: response.eventType,
            amount: response.amount,
            message: response.message,
            planName,
          });
        } else {
          toastUtils.error(response.message || '플랜 처리에 실패했습니다.');
        }
      } else if (mode === 'create' && isFree) {
        const response = await billingService.createFreeSubscription({ companyId, productId });
        if (response.success) {
          setPlanResult({ type: 'free', planName });
        } else {
          toastUtils.error(response.message || '플랜 처리에 실패했습니다.');
        }
      } else {
        const response = await billingService.createSubscription({ companyId, productId });
        if (response.success) {
          setPlanResult({
            type: 'paid',
            eventType: response.eventType,
            amount: response.amount,
            message: response.message,
            planName,
          });
        } else {
          toastUtils.error(response.message || '플랜 처리에 실패했습니다.');
        }
      }
    } catch {
      toastUtils.error('플랜 처리 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);      
    }
  };

  const handleResultConfirm = async () => {
    await loadBillingData(companyId);
    onComplete?.();
    setPlanResult(null);
    onClose();
  };

  const handleModalClose = () => {
    if (planResult) {
      handleResultConfirm();
      return;
    }
    onClose();
  };

  const renderPlanCard = (plan: BillingProductItem) => {
    const isCurrentPlan = plan.id === currentProductId;
    const isFree = plan.price === 0;
    const isConsultation = plan.isConsultationRequired ?? false;
    const isRecommended = recommendedProductId != null
      ? plan.id === recommendedProductId
      : plan.name === '프로';
    const isCare = plan.id === 5;
    const isEnterprise = plan.name === '엔터프라이즈';

    return (
      <PlanCard 
        key={plan.id} 
        $isCurrent={isCurrentPlan} 
        $isRecommended={isRecommended}
        $isCare={isCare}
        $isEnterprise={isEnterprise}
      >
        {isCurrentPlan && <Badge $variant="current">현재 사용 중</Badge>}
        {isRecommended && !isCurrentPlan && (
          <Badge $variant="recommended">
            <Sparkles size={12} />
            {recommendedProductId != null && plan.id === recommendedProductId ? '추천 플랜' : '가장 인기있는 플랜'}
          </Badge>
        )}
        {isConsultation && !isCurrentPlan && !isRecommended && (
          <Badge $variant="consultation">
            <Building2 size={12} />
            맞춤형 견적 제안
          </Badge>
        )}

        <CardContent>
          <PlanHeader>
            <PlanNameWrapper>
              <PlanName $isCare={isCare}>{plan.name}</PlanName>
              {plan.subName && <PlanSubName $isCare={isCare}>{plan.subName}</PlanSubName>}
            </PlanNameWrapper>
            <PlanDescription $isCare={isCare}>{plan.description ?? '준비 중인 플랜입니다.'}</PlanDescription>
          </PlanHeader>

          <PriceSection>
            {isConsultation ? (
              <ConsultationText $isCare={isCare}>
                <MessageSquare size={20} />
                별도 협의
              </ConsultationText>
            ) : isFree ? (
              <PriceAmount $isCare={isCare}>Free</PriceAmount>
            ) : (
              <PriceWrapper>
                <Currency $isCare={isCare}>{plan.priceUnit}</Currency>
                <Amount $isCare={isCare}>{plan.price.toLocaleString()}</Amount>
                <Period $isCare={isCare}>/ 월</Period>
              </PriceWrapper>
            )}
          </PriceSection>

          <FeatureList $isCare={isCare}>
            {plan.limitValue != null && (
              <FeatureItem $isCare={isCare}>
                <CheckIconWrapper $isCare={isCare}>
                  <Check size={14} />
                </CheckIconWrapper>
                <FeatureText $isCare={isCare}>포트폴리오 <b>{plan.limitValue}개</b> 이하</FeatureText>
              </FeatureItem>
            )}
            {plan.detailDescription?.split('. ').map((feature: string, idx: number) => {
              if (!feature.trim()) return null;
              const cleanedFeature = feature.trim().endsWith('.') ? feature.trim() : `${feature.trim()}.`;
              return (
                <FeatureItem key={idx} $isCare={isCare}>
                  <CheckIconWrapper $isCare={isCare}>
                    <Check size={14} />
                  </CheckIconWrapper>
                  <FeatureText $isCare={isCare}>{cleanedFeature}</FeatureText>
                </FeatureItem>
              );
            }) || (
              <FeatureItem $isCare={isCare}>
                <CheckIconWrapper $isCare={isCare}>
                  <Check size={14} />
                </CheckIconWrapper>
                <FeatureText $isCare={isCare}>모든 핵심 기능 포함</FeatureText>
              </FeatureItem>
            )}
          </FeatureList>

          <ActionWrapper>
            <SelectButton
              disabled={isCurrentPlan || isProcessing}
              $isCurrent={isCurrentPlan}
              $variant={isRecommended || isCare ? 'primary' : 'secondary'}
              $isCare={isCare}
              onClick={() => handleSelectPlan(plan.id, isFree, isConsultation, plan.name)}
            >
              {isProcessing ? (
                <Loader2 size={18} className="animate-spin" />
              ) : isCurrentPlan ? (
                '사용 중인 플랜'
              ) : isConsultation ? (
                '도입 문의하기'
              ) : (
                <>
                  {mode === 'create' ? '지금 시작하기' : '이 플랜으로 변경'}
                  <ArrowRight size={16} />
                </>
              )}
            </SelectButton>
          </ActionWrapper>
        </CardContent>
      </PlanCard>
    );
  };

  return (
    <>
    <ModalCenter
      isOpen={isOpen}
      onClose={handleModalClose}
      title={planResult ? '플랜 변경 완료' : '서비스 플랜 선택'}
    >
      {planResult ? (
        <ResultContainer>
          <ResultContent>
            {planResult.type === 'free' ? (
              <>
                <ResultIconWrapper $variant="success">
                  <CheckCircle2 size={48} />
                </ResultIconWrapper>
                <ResultTitle>무료 구독이 시작되었습니다</ResultTitle>
                <ResultPlanName>{planResult.planName} 플랜</ResultPlanName>
                <ResultDescription>
                  베이직 플랜으로 무료 구독이 시작되었습니다.<br />
                  감사합니다.
                </ResultDescription>
              </>
            ) : planResult.eventType === 'CHARGE' ? (
              <>
                <ResultIconWrapper $variant="success">
                  <CreditCard size={48} />
                </ResultIconWrapper>
                <ResultTitle>{planResult.planName} 플랜으로 변경되었습니다</ResultTitle>
                {planResult.amount != null && (
                  <ResultAmountBadge $variant="charge">
                    결제 금액: {planResult.amount.toLocaleString()}원
                  </ResultAmountBadge>
                )}
                {planResult.message && (
                  <ResultMessage>{planResult.message}</ResultMessage>
                )}
              </>
            ) : planResult.eventType === 'REFUND' ? (
              <>
                <ResultIconWrapper $variant="refund">
                  <RotateCcw size={48} />
                </ResultIconWrapper>
                <ResultTitle>{planResult.planName} 플랜으로 변경되었습니다</ResultTitle>
                {planResult.amount != null && (
                  <ResultAmountBadge $variant="refund">
                    환불 금액: {planResult.amount.toLocaleString()}원
                  </ResultAmountBadge>
                )}
                {planResult.message && (
                  <ResultMessage>{planResult.message}</ResultMessage>
                )}
              </>
            ) : (
              <>
                <ResultIconWrapper $variant="success">
                  <CheckCircle2 size={48} />
                </ResultIconWrapper>
                <ResultTitle>{planResult.planName} 플랜 구독이 시작되었습니다</ResultTitle>
                {planResult.message && (
                  <ResultMessage>{planResult.message}</ResultMessage>
                )}
              </>
            )}

            <ResultConfirmButton onClick={handleResultConfirm}>
              확인
            </ResultConfirmButton>
          </ResultContent>
        </ResultContainer>
      ) : (
        <Container>
          <TopBackground />
          
          <ContentWrapper>
            <HeaderSection>
              <HeaderTag>Service Plans</HeaderTag>
              <MainTitle>
                효율적인 투자를 위한 <br />
                <HighlightText>최고의 파트너</HighlightText>를 선택하세요
              </MainTitle>
              <SubTitle>
                복잡한 투자 관리는 팩트시트에게 맡기고, <br />
                귀사는 오직 <b>투자 본질</b>에만 집중하세요.
              </SubTitle>
            </HeaderSection>

            {isLoading || plans.length === 0 ? (
              <LoadingWrapper>
                <Loader2 size={40} className="animate-spin" />
                <span>플랜 정보를 구성하는 중입니다...</span>
              </LoadingWrapper>
            ) : (
              <MainContent>
                <Section>
                  <SectionHeader>
                    <SectionIconWrapper>
                      <Building2 size={20} />
                    </SectionIconWrapper>
                    <SectionInfo>
                      <SectionTitle>직접 관리형 플랜</SectionTitle>
                      <SectionDesc>귀사의 담당자가 플랫폼을 활용해 직접 관리합니다.</SectionDesc>
                    </SectionInfo>
                  </SectionHeader>
                  
                  <PlanGrid>
                    {selfManagedPlans.map(plan => renderPlanCard(plan))}
                  </PlanGrid>
                </Section>

                {carePlans.length > 0 && (
                  <Section>
                    <SectionHeader>
                      <SectionIconWrapper $isCare>
                        <Crown size={20} />
                      </SectionIconWrapper>
                      <SectionInfo>
                        <SectionTitle $isCare>전문가 케어 서비스</SectionTitle>
                        <SectionDesc>팩트시트 전문 운영팀이 모든 관리 업무를 전담합니다.</SectionDesc>
                      </SectionInfo>
                    </SectionHeader>
                    
                    <CareSectionWrapper>
                      {carePlans.map(plan => renderPlanCard(plan))}
                    </CareSectionWrapper>
                  </Section>
                )}

                <TrustSection>
                  <TrustItem>
                    <ShieldCheck size={24} color={colorTokens.brand[600]} />
                    <TrustInfo>
                      <TrustTitle>철저한 데이터 보안</TrustTitle>
                      <TrustText>금융권 수준의 암호화 및 다중 백업 시스템을 운영합니다.</TrustText>
                    </TrustInfo>
                  </TrustItem>
                  <TrustItem>
                    <Zap size={24} color={colorTokens.brand[600]} />
                    <TrustInfo>
                      <TrustTitle>유연한 플랜 변경</TrustTitle>
                      <TrustText>언제든지 위아래로 플랜을 변경하거나 해지할 수 있습니다.</TrustText>
                    </TrustInfo>
                  </TrustItem>
                </TrustSection>

                <RefundPolicyToggle onClick={() => setIsRefundPolicyOpen(prev => !prev)}>
                  <RefundPolicyToggleLeft>
                    <FileText size={16} />
                    환불 규정 안내
                  </RefundPolicyToggleLeft>
                  <RefundPolicyChevron $isOpen={isRefundPolicyOpen}>
                    <ChevronRight size={16} />
                  </RefundPolicyChevron>
                </RefundPolicyToggle>

                {isRefundPolicyOpen && (
                  <RefundPolicyContent>
                    <RefundPolicyTitle>제15조 (유료서비스의 이용, 해지, 환불)</RefundPolicyTitle>
                    <RefundPolicyList>
                      <li>회사는 이용자에게 유료서비스를 제공하며, 유료서비스의 이용 요금, 결제 방식 및 제공 내용은 서비스 화면에 별도로 고지합니다.</li>
                      <li>이용자는 언제든지 서비스 내 제공되는 방법을 통해 유료서비스 이용을 해지할 수 있으며, 해지는 다음 결제 예정일부터 적용됩니다.</li>
                      <li>회원은 회사에 환불을 요구할 수 있습니다. 환불은 회사가 안내하는 정책 및 방법에 따라 진행됩니다.</li>
                      <li>
                        회사는 다음 각 호의 방식으로 환불을 진행합니다.
                        <RefundPolicySubList>
                          <li>정기 결제 회원 : 환불은 서비스를 이용한 일수를 제외하고 일할 계산으로 진행됩니다. 월 기준은 30일이고 월 이용료를 30으로 나눈 금액을 말합니다. 결제 시간으로부터 24시간 이내 사용자는 잔액 환불처리되며, 결제 시간으로부터 24시간 이후부터 15일까지는 남은 일 수에 대한 일할 계산으로 환불 처리됩니다. 16일 이후 30일 이내 사용자는 환불이 불가능합니다. 일할 계산은 카드 수수료와 위약금을 포함한 결제 금액의 20% 금액을 제외한 금액이며, 남은 일 수에 대한 일할 계산으로 환불 처리됩니다.</li>
                          <li>계산 공식 : [(전체결제금액 × 0.8 / 30) × 남은 일수]</li>
                          <li>연간 결제 회원 : 연 기준은 12개월이고 잔여 이용료는 전체 연간결제 이용료를 12로 나누고 하루라도 이용한 월은 제외하고 남은 금액을 환불합니다.</li>
                        </RefundPolicySubList>
                      </li>
                      <li>본 조의 환불 금액 기준은 연간 결제 회원이라 하더라도 정기결제금액으로 계산 후 진행됩니다. 따라서 환불 시점에 따라 환불금액이 존재하지 않는 경우도 있을 수 있습니다.</li>
                    </RefundPolicyList>
                  </RefundPolicyContent>
                )}
              </MainContent>
            )}
          </ContentWrapper>
        </Container>
      )}
    </ModalCenter>

    {consultationModal.isOpen && (
      <ConsultationOverlay onClick={handleCloseConsultationModal}>
        <ConsultationModalWrapper onClick={(e) => e.stopPropagation()}>
          <ConsultationHeader>
            <ConsultationHeaderLeft>
              <ConsultationIconBadge>
                <MessageSquare size={22} />
              </ConsultationIconBadge>
              <ConsultationHeaderText>
                <ConsultationTitle>별도 협의 문의</ConsultationTitle>
                <ConsultationPlanTag>{consultationModal.planName} 플랜</ConsultationPlanTag>
              </ConsultationHeaderText>
            </ConsultationHeaderLeft>
            <ConsultationCloseButton onClick={handleCloseConsultationModal}>
              <X size={20} />
            </ConsultationCloseButton>
          </ConsultationHeader>

          <ConsultationBody>
            <ConsultationDescription>
              <b>{consultationModal.planName}</b> 플랜은 별도 협의가 필요한 맞춤형 상품입니다.<br />
              아래에 문의 내용을 남겨주시면 담당자가 빠르게 연락드리겠습니다.
            </ConsultationDescription>

            <ConsultationFormGroup>
              <ConsultationLabel>문의 내용 <ConsultationOptional>(선택)</ConsultationOptional></ConsultationLabel>
              <ConsultationTextarea
                value={consultationMessage}
                onChange={(e) => setConsultationMessage(e.target.value)}
                placeholder="도입 규모, 요구사항, 희망 일정 등 자유롭게 작성해주세요."
                maxLength={500}
                rows={5}
              />
              <ConsultationCharCount>
                {consultationMessage.length} / 500
              </ConsultationCharCount>
            </ConsultationFormGroup>
          </ConsultationBody>

          <ConsultationFooter>
            <ConsultationCancelButton onClick={handleCloseConsultationModal}>
              취소
            </ConsultationCancelButton>
            <ConsultationSubmitButton
              onClick={handleSubmitConsultation}
              disabled={isSubmittingConsultation}
            >
              {isSubmittingConsultation ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Send size={16} />
                  문의 보내기
                </>
              )}
            </ConsultationSubmitButton>
          </ConsultationFooter>
        </ConsultationModalWrapper>
      </ConsultationOverlay>
    )}
    </>
  );
};

// Styled Components
const Container = styled.div`
  width: 80vw;;
  position: relative;
  background: #f8fafc;
  overflow: hidden;
`;

const TopBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 480px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  clip-path: polygon(0 0, 100% 0, 100% 85%, 0% 100%);
  z-index: 0;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding: 4rem 2rem 5rem;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 5rem;
`;

const HeaderTag = styled.div`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: white;
  color: ${colorTokens.brand[600]};
  border-radius: 100px;
  font-size: 0.813rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  margin-bottom: 1.5rem;
`;

const MainTitle = styled.h2`
  ${typography.heading.h1};
  font-size: 2.75rem;
  line-height: 1.2;
  color: #0f172a;
  font-weight: 850;
  margin-bottom: 1.5rem;
  letter-spacing: -0.03em;
`;

const HighlightText = styled.span`
  background: linear-gradient(135deg, ${colorTokens.brand[600]}, ${colorTokens.brand[800]});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SubTitle = styled.p`
  ${typography.paragraph.p1.default};
  font-size: 1.125rem;
  color: #475569;
  line-height: 1.6;
  
  b {
    color: #0f172a;
    font-weight: 700;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding-left: 0.5rem;
`;

const SectionIconWrapper = styled.div<{ $isCare?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${props => props.$isCare ? '#1e293b' : 'white'};
  color: ${props => props.$isCare ? '#f8fafc' : colorTokens.brand[600]};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const SectionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SectionTitle = styled.h3<{ $isCare?: boolean }>`
  ${typography.heading.h4};
  color: ${props => props.$isCare ? '#0f172a' : '#1e293b'};
  font-weight: 800;
  margin: 0;
`;

const SectionDesc = styled.p`
  ${typography.caption.caption1.default};
  color: #64748b;
  margin: 0;
`;

const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1440px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const CareSectionWrapper = styled.div`
  max-width: 500px;
`;

const PlanCard = styled.div<{ $isCurrent: boolean; $isRecommended: boolean; $isCare?: boolean; $isEnterprise?: boolean }>`
  background: ${props => props.$isCare ? '#1e293b' : 'white'};
  border-radius: 32px;
  padding: 3rem 2.25rem;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  border: 1px solid ${props => 
    props.$isCurrent ? colorTokens.brand[500] : 
    props.$isCare ? '#334155' :
    props.$isEnterprise ? '#cbd5e1' :
    '#f1f5f9'
  };
  box-shadow: ${props => 
    props.$isRecommended ? '0 30px 60px -12px rgba(16, 185, 129, 0.15)' : 
    props.$isEnterprise ? '0 20px 40px -10px rgba(15, 23, 42, 0.05)' :
    '0 10px 25px -5px rgba(0, 0, 0, 0.02)'
  };

  ${props => (props.$isRecommended || props.$isEnterprise) && !props.$isCare && css`
    &::after {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 34px;
      padding: 2px;
      background: ${props.$isRecommended 
        ? `linear-gradient(135deg, ${colorTokens.brand[400]}, ${colorTokens.brand[700]})`
        : 'linear-gradient(135deg, #cbd5e1, #94a3b8)'};
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
      opacity: ${props.$isEnterprise ? 0.4 : 1};
    }
  `}

  &:hover {
    transform: translateY(-12px);
    box-shadow: ${props => props.$isCare ? '0 40px 80px -15px rgba(15, 23, 42, 0.4)' : '0 40px 80px -15px rgba(0, 0, 0, 0.08)'};
    border-color: ${props => props.$isCare ? '#475569' : colorTokens.brand[300]};
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Badge = styled.div<{ $variant: 'current' | 'recommended' | 'consultation' }>`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.5rem 1.25rem;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  z-index: 2;

  ${props => props.$variant === 'current' && css`
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
  `}

  ${props => props.$variant === 'recommended' && css`
    background: linear-gradient(135deg, ${colorTokens.brand[600]}, ${colorTokens.brand[700]});
    color: white;
  `}

  ${props => props.$variant === 'consultation' && css`
    background: linear-gradient(135deg, #475569, #1e293b);
    color: #f8fafc;
    border: 1px solid rgba(255, 255, 255, 0.1);
  `}
`;

const PlanHeader = styled.div`
  margin-bottom: 2.5rem;
`;

const PlanNameWrapper = styled.div`
  margin-bottom: 0.75rem;
`;

const PlanName = styled.h4<{ $isCare?: boolean }>`
  font-size: 1.75rem;
  font-weight: 850;
  color: ${props => props.$isCare ? '#f8fafc' : '#0f172a'};
  margin: 0;
  letter-spacing: -0.02em;
`;

const PlanSubName = styled.span<{ $isCare?: boolean }>`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => props.$isCare ? colorTokens.brand[400] : colorTokens.brand[600]};
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const PlanDescription = styled.p<{ $isCare?: boolean }>`
  ${typography.paragraph.p2.default};
  color: ${props => props.$isCare ? '#94a3b8' : '#64748b'};
  line-height: 1.6;
  margin: 0;
  min-height: 3rem;
`;

const PriceSection = styled.div`
  margin-bottom: 3rem;
  min-height: 4rem;
  display: flex;
  align-items: center;
`;

const PriceAmount = styled.div<{ $isCare?: boolean }>`
  font-size: 3rem;
  font-weight: 850;
  color: ${props => props.$isCare ? '#f8fafc' : '#0f172a'};
  letter-spacing: -0.04em;
`;

const PriceWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const Currency = styled.span<{ $isCare?: boolean }>`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.$isCare ? '#94a3b8' : '#64748b'};
`;

const Amount = styled.span<{ $isCare?: boolean }>`
  font-size: 3rem;
  font-weight: 850;
  color: ${props => props.$isCare ? '#f8fafc' : '#0f172a'};
  letter-spacing: -0.04em;
`;

const Period = styled.span<{ $isCare?: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.$isCare ? '#64748b' : '#94a3b8'};
  margin-left: 0.25rem;
`;

const ConsultationText = styled.div<{ $isCare?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 2rem;
  font-weight: 850;
  color: ${props => props.$isCare ? '#f8fafc' : '#0f172a'};
  letter-spacing: -0.02em;
`;

const FeatureList = styled.div<{ $isCare?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
  margin-bottom: 3.5rem;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.$isCare ? '#334155' : '#f1f5f9'};
`;

const FeatureItem = styled.div<{ $isCare?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const CheckIconWrapper = styled.div<{ $isCare?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.$isCare ? 'rgba(16, 185, 129, 0.15)' : colorTokens.brand[50]};
  color: ${colorTokens.brand[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

const FeatureText = styled.span<{ $isCare?: boolean }>`
  font-size: 0.938rem;
  line-height: 1.5;
  color: ${props => props.$isCare ? '#cbd5e1' : '#334155'};
  
  b {
    color: ${props => props.$isCare ? '#f8fafc' : '#0f172a'};
    font-weight: 700;
  }
`;

const ActionWrapper = styled.div`
  margin-top: auto;
`;

const SelectButton = styled.button<{ $isCurrent: boolean; $variant: 'primary' | 'secondary'; $isCare?: boolean }>`
  width: 100%;
  height: 60px;
  border-radius: 20px;
  border: none;
  font-size: 1.063rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  cursor: pointer;

  ${props => props.$isCurrent ? css`
    background: ${props.$isCare ? '#334155' : '#f1f5f9'};
    color: ${props.$isCare ? '#94a3b8' : '#94a3b8'};
    cursor: default;
  ` : props.$variant === 'primary' ? css`
    background: ${props.$isCare ? 'white' : `linear-gradient(135deg, ${colorTokens.brand[600]}, ${colorTokens.brand[700]})`};
    color: ${props.$isCare ? '#0f172a' : 'white'};
    box-shadow: ${props.$isCare ? '0 10px 20px rgba(255, 255, 255, 0.1)' : '0 10px 20px rgba(16, 185, 129, 0.2)'};

    &:hover {
      transform: scale(1.02);
      filter: brightness(1.1);
      box-shadow: ${props.$isCare ? '0 15px 30px rgba(255, 255, 255, 0.15)' : '0 15px 30px rgba(16, 185, 129, 0.3)'};
    }
  ` : css`
    background: ${props.$isCare ? '#334155' : '#1e293b'};
    color: white;

    &:hover {
      transform: scale(1.02);
      background: ${props.$isCare ? '#475569' : '#0f172a'};
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }
  `}

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const TrustSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  padding: 3rem;
  background: white;
  border-radius: 32px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const TrustItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const TrustInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TrustTitle = styled.h5`
  font-size: 1.125rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
`;

const TrustText = styled.p`
  font-size: 0.938rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 500px;
  gap: 2rem;
  color: #94a3b8;
  
  span {
    font-size: 1.125rem;
    font-weight: 600;
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Consultation Modal Styled Components
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const ConsultationOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ConsultationModalWrapper = styled.div`
  background: white;
  border-radius: 24px;
  width: 480px;
  max-width: calc(100vw - 2rem);
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.15);
  animation: ${slideUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ConsultationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid #f1f5f9;
`;

const ConsultationHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ConsultationIconBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${colorTokens.brand[50]}, ${colorTokens.brand[100]});
  color: ${colorTokens.brand[600]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ConsultationHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ConsultationTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
`;

const ConsultationPlanTag = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${colorTokens.brand[600]};
  background: ${colorTokens.brand[50]};
  padding: 0.125rem 0.5rem;
  border-radius: 6px;
  width: fit-content;
`;

const ConsultationCloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: #f8fafc;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

const ConsultationBody = styled.div`
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ConsultationDescription = styled.p`
  font-size: 0.938rem;
  color: #475569;
  line-height: 1.7;
  margin: 0;

  b {
    color: #0f172a;
    font-weight: 700;
  }
`;

const ConsultationFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ConsultationLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e293b;
`;

const ConsultationOptional = styled.span`
  font-weight: 500;
  color: #94a3b8;
`;

const ConsultationTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  font-size: 0.938rem;
  font-family: inherit;
  color: #0f172a;
  background: #f8fafc;
  resize: vertical;
  min-height: 120px;
  line-height: 1.6;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: ${colorTokens.brand[400]};
    background: white;
    box-shadow: 0 0 0 3px ${colorTokens.brand[50]};
  }
`;

const ConsultationCharCount = styled.span`
  font-size: 0.75rem;
  color: #94a3b8;
  text-align: right;
`;

const ConsultationFooter = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1.25rem 1.75rem;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
`;

const ConsultationCancelButton = styled.button`
  flex: 1;
  height: 48px;
  border-radius: 14px;
  border: 1.5px solid #e2e8f0;
  background: white;
  color: #475569;
  font-size: 0.938rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const ConsultationSubmitButton = styled.button`
  flex: 1.5;
  height: 48px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, ${colorTokens.brand[600]}, ${colorTokens.brand[700]});
  color: white;
  font-size: 0.938rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);

  &:hover:not(:disabled) {
    filter: brightness(1.1);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const resultFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ResultContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 60px 40px;
`;

const ResultContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  animation: ${resultFadeIn} 0.5s ease-out;
`;

const ResultIconWrapper = styled.div<{ $variant: 'success' | 'refund' }>`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  ${({ $variant }) =>
    $variant === 'refund'
      ? css`
          background: ${colorTokens.blue[100]};
          color: ${colorTokens.blue[500]};
        `
      : css`
          background: ${colorTokens.green[100]};
          color: ${colorTokens.green[500]};
        `}
`;

const ResultTitle = styled.h2`
  ${typography.heading.h4};
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const ResultPlanName = styled.span`
  ${typography.paragraph.p1.medium};
  color: ${semanticColors.text.secondary};
  background: ${colorTokens.gray[100]};
  padding: 4px 14px;
  border-radius: 20px;
`;

const ResultDescription = styled.p`
  ${typography.paragraph.p1.default};
  color: ${semanticColors.text.secondary};
  line-height: 1.7;
  margin: 8px 0 0;
`;

const ResultAmountBadge = styled.div<{ $variant: 'charge' | 'refund' }>`
  ${typography.subtitle.subtitle2};
  padding: 8px 20px;
  border-radius: 8px;
  ${({ $variant }) =>
    $variant === 'refund'
      ? css`
          background: ${colorTokens.blue[100]};
          color: ${colorTokens.blue[600]};
        `
      : css`
          background: ${colorTokens.green[100]};
          color: ${colorTokens.green[600]};
        `}
`;

const ResultMessage = styled.p`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.tertiary};
  margin: 0;
  max-width: 360px;
  line-height: 1.6;
`;

const ResultConfirmButton = styled.button`
  margin-top: 16px;
  padding: 12px 48px;
  border: none;
  border-radius: 10px;
  background: ${colorTokens.brand[600]};
  color: white;
  ${typography.subtitle.subtitle2};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colorTokens.brand[700]};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const RefundPolicyToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 18px;
  border: 1px solid ${colorTokens.gray[200]};
  border-radius: 10px;
  background: ${colorTokens.gray[100]};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 4px;

  &:hover {
    background: ${colorTokens.gray[200]};
  }
`;

const RefundPolicyToggleLeft = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  ${typography.paragraph.p2.medium};
  color: ${semanticColors.text.secondary};
`;

const RefundPolicyChevron = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  color: ${semanticColors.text.tertiary};
  transition: transform 0.2s ease;
  transform: rotate(${({ $isOpen }) => ($isOpen ? '90deg' : '0deg')});
`;

const RefundPolicyContent = styled.div`
  padding: 20px 24px;
  border: 1px solid ${colorTokens.gray[200]};
  border-radius: 10px;
  background: white;
  animation: ${resultFadeIn} 0.3s ease-out;
`;

const RefundPolicyTitle = styled.h4`
  ${typography.subtitle.subtitle2};
  color: ${semanticColors.text.primary};
  margin: 0 0 14px;
`;

const RefundPolicyList = styled.ol`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  li {
    ${typography.caption.caption1.default};
    color: ${semanticColors.text.secondary};
    line-height: 1.7;
  }
`;

const RefundPolicySubList = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  list-style-type: disc;
  display: flex;
  flex-direction: column;
  gap: 6px;

  li {
    ${typography.caption.caption1.default};
    color: ${semanticColors.text.tertiary};
    line-height: 1.7;
  }
`;

export default PlanSelectionModal;
